import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  VStack,
  HStack,
  Box,
  Text,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  useColorModeValue,
  FormControl,
  FormLabel,
  useToast,
  Switch,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { getAllFeatures, addFeature } from '../firebase/features';
import { featureCategories as staticFeatureCategories, addFeatureToCategory } from '../data/filterOptions'; // Renamed static import
import BoundingBoxModal from './media/BoundingBoxModal';
import { MagnifyingGlass } from '@phosphor-icons/react';

export default function FeatureTaggingModal({
  isOpen,
  onClose,
  onFeatureSelect,
  onFeatureBoxesDrawn,
  currentPredictions = [],
  isLoading = false,
  media
}) {
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentCategoryFilter, setCurrentCategoryFilter] = useState('');
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureAssignedCategory, setNewFeatureAssignedCategory] = useState('');
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [dynamicCategories, setDynamicCategories] = useState([]); // For dropdown
  const [drawBoundingBox, setDrawBoundingBox] = useState(true);
  const [showBoundingBoxModal, setShowBoundingBoxModal] = useState(false);
  const [currentFeatureForModal, setCurrentFeatureForModal] = useState(null);
  const toast = useToast();

  const tagBg = 'bg.accent';
  const selectedTagBg = useColorModeValue('brand.primary.100', 'brand.primary.700');
  const categoryBg = 'bg.accent';
  const headerBg = 'bg.accent';

  useEffect(() => {
    if (!isOpen) {
      setSelectedFeatures([]);
      setSearch('');
      setNewFeatureName('');
      setNewFeatureAssignedCategory('');
      setCustomCategoryName('');
      setDrawBoundingBox(true);
      setCurrentCategoryFilter('');
      setCurrentFeatureForModal(null);
      return;
    }
    loadFeaturesAndCategories();
  }, [isOpen]);

  const loadFeaturesAndCategories = async () => {
    setLoading(true);
    try {
      const featuresResult = await getAllFeatures();
      let currentAvailableFeatures = {};
      if (featuresResult.success && featuresResult.data) {
        currentAvailableFeatures = featuresResult.data;
        setAvailableFeatures(currentAvailableFeatures);
      } else {
        setAvailableFeatures({});
      }

      // Populate dynamicCategories from all available features
      const categoryMap = new Map();
      Object.values(currentAvailableFeatures).forEach(feature => {
        if (feature.category) {
          if (!categoryMap.has(feature.category)) {
            // Try to get a nice label from staticFeatureCategories, else title-case the key
            const staticLabel = staticFeatureCategories[feature.category]?.label;
            categoryMap.set(feature.category, staticLabel || feature.category.charAt(0).toUpperCase() + feature.category.slice(1).replace(/_/g, ' '));
          }
        }
      });
      // Add static categories that might not have features yet
      Object.entries(staticFeatureCategories).forEach(([key, value]) => {
        if (!categoryMap.has(key)) {
          categoryMap.set(key, value.label);
        }
      });
      
      const sortedCategories = Array.from(categoryMap.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setDynamicCategories(sortedCategories);


      // Initialize selectedFeatures based on currentPredictions
      if (currentPredictions?.length > 0) {
        const autoSelected = currentPredictions
          .filter(pred => pred.confidence > 0.5)
          .map(pred => {
            const predClassLower = pred.class?.toLowerCase();
            const foundFeature = Object.values(currentAvailableFeatures).find(
              f => f.id === pred.class || f.id === predClassLower || f.label?.toLowerCase() === predClassLower
            );
            return {
              id: foundFeature?.id || pred.class?.toLowerCase().replace(/\s+/g, '_') || `unknown_${Date.now()}`,
              label: foundFeature?.label || pred.class || 'Unknown',
              category: foundFeature?.category || 'uncategorized',
              source: pred.source || 'auto',
              confidence: pred.confidence,
              bbox: pred.bbox ? { ...pred.bbox } : null
            };
          });
        setSelectedFeatures(autoSelected);
      } else {
        setSelectedFeatures([]);
      }

    } catch (error) {
      console.error('Error loading features and categories:', error);
      toast({ title: "Error", description: "Failed to load feature data.", status: "error", duration: 3000 });
      setAvailableFeatures({});
      setSelectedFeatures([]);
      setDynamicCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomFeature = async () => {
    const featureLabel = newFeatureName.trim();
    let categoryToSave = newFeatureAssignedCategory;

    if (categoryToSave === 'other') {
      categoryToSave = customCategoryName.trim().toLowerCase().replace(/\s+/g, '_');
      if (!categoryToSave) {
        toast({ title: "Error", description: "Please enter a name for the new category.", status: "error", duration: 3000 });
        return;
      }
    }

    if (!featureLabel || !categoryToSave) {
      toast({ title: "Error", description: "Please provide a feature name and select/enter a category.", status: "error", duration: 3000 });
      return;
    }

    setLoading(true);
    try {
      const result = await addFeature({ label: featureLabel, source: 'manual', category: categoryToSave });
      if (result.success && result.data) {
        // No need to manually update staticFeatureCategories if dynamic list is robust
        // addFeatureToCategory(result.data.id, categoryToSave); 
        await loadFeaturesAndCategories(); // This will refresh availableFeatures and dynamicCategories
        toast({
          title: "Success",
          description: `Feature "${result.data.label}" added to category "${categoryToSave}".`,
          status: "success",
          duration: 3000
        });
        setNewFeatureName('');
        setNewFeatureAssignedCategory('');
        setCustomCategoryName('');
      } else {
        throw new Error(result.error || "Failed to add feature.");
      }
    } catch (error) {
      toast({ title: "Error", description: error.message || "Failed to add custom feature.", status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (featureId, featureDetails) => {
    const isAutoDetectedPred = currentPredictions.find(p => (p.class === featureId || p.class?.toLowerCase() === featureDetails.label?.toLowerCase()) && p.source === 'auto');
    setCurrentFeatureForModal({
      id: featureDetails.id,
      label: featureDetails.label,
      category: featureDetails.category,
      source: isAutoDetectedPred ? 'auto' : 'manual',
      confidence: isAutoDetectedPred?.confidence || 1,
      isAutoDetected: !!isAutoDetectedPred
    });

    if (drawBoundingBox) setShowBoundingBoxModal(true);
    else {
      const newSelectedFeature = {
        id: featureDetails.id,
        label: featureDetails.label,
        category: featureDetails.category,
        source: isAutoDetectedPred ? 'auto' : 'manual',
        bbox: isAutoDetectedPred?.bbox ? { ...isAutoDetectedPred.bbox } : null,
        confidence: isAutoDetectedPred?.confidence || 1
      };
      setSelectedFeatures(prev => prev.find(f => f.id === newSelectedFeature.id) ? prev : [...prev, newSelectedFeature]);
      onFeatureSelect?.(newSelectedFeature);
    }
  };

  const handleBoundingBoxComplete = (boxes) => {
    if (!currentFeatureForModal || !boxes.length) return;
    const box = boxes[0];
    const newSelectedFeature = {
      id: currentFeatureForModal.id,
      label: currentFeatureForModal.label,
      category: currentFeatureForModal.category,
      source: 'manual', 
      bbox: { x: box.x, y: box.y, width: box.width, height: box.height },
      confidence: 1 
    };
    setSelectedFeatures(prev => {
      const existingIndex = prev.findIndex(f => f.id === newSelectedFeature.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...newSelectedFeature };
        return updated;
      }
      return [...prev, newSelectedFeature];
    });
    onFeatureSelect?.(newSelectedFeature);
    setShowBoundingBoxModal(false);
    setCurrentFeatureForModal(null);
  };

  const handleSave = () => {
    const finalPredictions = selectedFeatures.map(f => ({
      class: f.id, label: f.label, category: f.category,
      confidence: f.confidence || 1, source: f.source || 'manual',
      x: f.bbox?.x || 0, y: f.bbox?.y || 0, width: f.bbox?.width || 0, height: f.bbox?.height || 0
    }));
    if (onFeatureBoxesDrawn) onFeatureBoxesDrawn(finalPredictions);
    onClose();
  };

  const getFilteredFeatures = () => {
    const searchLower = search.toLowerCase();
    const results = {};
    Object.values(availableFeatures).forEach(feature => {
      if (searchLower && !feature.label.toLowerCase().includes(searchLower)) return;
      if (currentCategoryFilter && feature.category !== currentCategoryFilter) return;
      const categoryKey = feature.category || 'uncategorized';
      if (!results[categoryKey]) {
        const catLabel = dynamicCategories.find(dc => dc.value === categoryKey)?.label || 
                         staticFeatureCategories[categoryKey]?.label || 
                         (categoryKey === 'uncategorized' ? 'Uncategorized' : categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1).replace(/_/g, ' '));
        results[categoryKey] = { label: catLabel, features: [] };
      }
      results[categoryKey].features.push(feature);
    });
    return results;
  };

  const filteredFeatures = getFilteredFeatures();
  const hasResults = Object.keys(filteredFeatures).length > 0;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Features</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg={headerBg} borderRadius="md">
                <Text fontSize="md" fontWeight="medium" mb={3}>Add New Feature</Text>
                <VStack spacing={3} align="stretch">
                  <Input
                    placeholder="Feature name (e.g., Column Base)"
                    value={newFeatureName}
                    onChange={(e) => setNewFeatureName(e.target.value)}
                    isDisabled={loading || isLoading}
                  />
                  <Select
                    placeholder="Select Category"
                    value={newFeatureAssignedCategory}
                    onChange={(e) => {
                      setNewFeatureAssignedCategory(e.target.value);
                      if (e.target.value !== 'other') setCustomCategoryName('');
                    }}
                    isDisabled={loading || isLoading}
                  >
                    {dynamicCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                    <option value="other">Other (Specify New)</option>
                  </Select>
                  {newFeatureAssignedCategory === 'other' && (
                    <Input
                      placeholder="Enter new category name (e.g., Pottery Styles)"
                      value={customCategoryName}
                      onChange={(e) => setCustomCategoryName(e.target.value)}
                      isDisabled={loading || isLoading}
                    />
                  )}
                  <Button
                    colorScheme="brand.primary"
                    onClick={handleAddCustomFeature}
                    isLoading={loading}
                    isDisabled={isLoading || !newFeatureName.trim() || !newFeatureAssignedCategory || (newFeatureAssignedCategory === 'other' && !customCategoryName.trim())}
                  >
                    Add Feature
                  </Button>
                </VStack>
              </Box>

              <Box bg={headerBg} p={4} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <InputGroup>
                    <InputLeftElement pointerEvents="none"><MagnifyingGlass /></InputLeftElement>
                    <Input placeholder="Search existing features..." value={search} onChange={(e) => setSearch(e.target.value)} bg="bg.surface" />
                  </InputGroup>
                  <HStack justify="space-between">
                    <FormLabel htmlFor="draw-bbox-switch" mb="0" fontSize="sm">Draw bounding boxes</FormLabel>
                    <Switch id="draw-bbox-switch" isChecked={drawBoundingBox} onChange={(e) => setDrawBoundingBox(e.target.checked)} />
                  </HStack>
                </VStack>
              </Box>

              {loading ? (
                <Text textAlign="center" color="text.muted">Loading features...</Text>
              ) : !hasResults ? (
                <Text textAlign="center" color="text.muted">No features found. Try adding one!</Text>
              ) : (
                <Accordion allowMultiple defaultIndex={Object.keys(filteredFeatures).length === 1 ? [0] : []}>
                  {Object.entries(filteredFeatures)
                    .sort(([, a], [,b]) => a.label.localeCompare(b.label))
                    .map(([categoryKey, categoryData]) => (
                    <AccordionItem key={categoryKey} borderWidth={1} borderColor="border.default" borderRadius="md" mb={2}>
                      <AccordionButton py={3} _hover={{ bg: categoryBg }}>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="medium">{categoryData.label}</Text>
                          <Text fontSize="sm" color="text.muted" mt={1}>{categoryData.features.length} features</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Wrap spacing={2}>
                          {categoryData.features
                            .sort((a,b) => a.label.localeCompare(b.label))
                            .map((feature) => {
                            const isSelected = selectedFeatures.some(f => f.id === feature.id);
                            return (
                              <WrapItem key={feature.id}>
                                <Tag
                                  size="md" borderRadius="full"
                                  variant={isSelected && !drawBoundingBox ? "solid" : "outline"}
                                  colorScheme={isSelected && !drawBoundingBox ? "brand.primary" : "gray"}
                                  bg={isSelected && !drawBoundingBox ? selectedTagBg : tagBg}
                                  cursor="pointer"
                                  onClick={() => handleSelect(feature.id, feature)}
                                  userSelect="none"
                                  _hover={{ transform: 'translateY(-1px)', shadow: 'sm' }}
                                >
                                  <TagLabel>{feature.label}</TagLabel>
                                </Tag>
                              </WrapItem>
                            );
                          })}
                        </Wrap>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand.primary" onClick={handleSave} isDisabled={isLoading}>Apply Features</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {showBoundingBoxModal && currentFeatureForModal && media && (
        <BoundingBoxModal
          isOpen={showBoundingBoxModal}
          onClose={() => setShowBoundingBoxModal(false)}
          onComplete={handleBoundingBoxComplete}
          imageUrl={media.url}
          featureKey={currentFeatureForModal.id}
          featureLabel={currentFeatureForModal.label}
        />
      )}
    </>
  );
}
